import { Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http'

import { environment } from '../../environments/environment'
import { LoginAnswer, User } from '../_models/'
import { APTechnicianHash } from '../_models/technician.interface'
import { PasskeyAuthenticationOptions } from '../_models/passkey.interface'
import { SKIP_ERROR_INTERCEPTOR } from '../_helpers/http-context.tokens'
import { isPasskeyCancellation, passkeyLoginErrorMessage } from '../_helpers/passkey.helpers'
import { LocalStorageService } from './localstore.service'
import { WebAuthnService } from './webauthn.service'
import { firstValueFrom } from 'rxjs'

// Ergebnis eines Passkey-Logins. „cancelled“ = Abbruch durch den Nutzer oder
// durch einen neueren WebAuthn-Aufruf, wird nicht als Fehler angezeigt.
export type PasskeyLoginResult =
  | { status: 'success' }
  | { status: 'cancelled' }
  | { status: 'failed'; message: string }

@Injectable({
  providedIn: 'root',
})
export class SessionService {

  public authToken: string
  public loginError: string = null

  constructor(
    private router: Router,
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private webAuthn: WebAuthnService
  ) { }

  public setCurrentUser(user: APTechnicianHash) {
    this.localStorageService.setItem('currentUser', user);
    this.localStorageService.setItem('currentRoles', user.roles);
  }

  public getCurrentUser(): APTechnicianHash {
    const currentUser = this.localStorageService.getItem<APTechnicianHash>('currentUser');
    if (currentUser) {
      return currentUser
    }
    return null
  }

  public removeCurrentUser(): void {
    this.localStorageService.removeItem('currentUser');
    this.localStorageService.removeItem('currentRoles');
    this.localStorageService.removeItem('loginValidUntil');
  }

  public login(email: string, password: string) {
    const path = environment.apiURL + 'login_hawk.json'
    const body = { email, password }

    return this.http.post<APTechnicianHash>(path, body)
  }

  public async loginRedirect(email: string, password: string, returnUrl: string) {
    const path = environment.apiURL + 'login_hawk.json'
    const body = { email, password }

    try {
      const data = await firstValueFrom(this.http.post<APTechnicianHash>(path, body));
      this.completeLogin(data, returnUrl);
    } catch (err: unknown) {
      console.error(err);

      if (err instanceof HttpErrorResponse) {
        this.loginError = err.message;
      } else {
        this.loginError = "Ein Fehler ist aufgetreten."
      }
    }
  }

  // Anmeldung per Passkey (siehe bentomax/hawk#596). Läuft über denselben
  // Endpunkt login_hawk wie der Passwort-Login, nur mit passkey-Body, und
  // endet in derselben Nachbehandlung.
  //
  // useBrowserAutofill = Conditional UI: Der Browser bietet die Passkeys im
  // E-Mail-Feld an, das Promise erfüllt sich erst nach der Auswahl.
  //
  // stillWanted wird nach dem Laden der Options gefragt. Liefert es false (der
  // Aufrufer hat inzwischen einen anderen Login gestartet), wird der
  // Browser-Dialog gar nicht erst geöffnet – sonst würde ein verspäteter
  // Autofill den gerade gestarteten Button-Aufruf abbrechen.
  public async loginWithPasskey(
    returnUrl: string = '/',
    useBrowserAutofill = false,
    stillWanted: () => boolean = () => true
  ): Promise<PasskeyLoginResult> {
    // Ein 401 heißt hier „Passkey abgelehnt“ und nicht „Session abgelaufen“,
    // deshalb am globalen Logout-Handling vorbei.
    const context = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)

    let options: PasskeyAuthenticationOptions
    try {
      options = await firstValueFrom(
        this.http.post<PasskeyAuthenticationOptions>(environment.apiURL + 'passkeys/authentication_options.json', {}, { context })
      );
    } catch (err: unknown) {
      console.error(err);
      // Autofill ist nur ein Angebot beim Laden der Seite – sind Passkeys
      // serverseitig aus oder der Server nicht erreichbar, bleibt es still.
      if (useBrowserAutofill) {
        return { status: 'cancelled' };
      }
      return { status: 'failed', message: passkeyLoginErrorMessage(err) };
    }

    if (!stillWanted()) {
      return { status: 'cancelled' };
    }

    try {
      const credential = await this.webAuthn.startAuthentication(options.options, useBrowserAutofill);
      const body = { passkey: { challenge_id: options.challenge_id, credential } };
      const data = await firstValueFrom(
        this.http.post<APTechnicianHash>(environment.apiURL + 'login_hawk.json', body, { context })
      );
      this.completeLogin(data, returnUrl);
      return { status: 'success' };
    } catch (err: unknown) {
      if (isPasskeyCancellation(err)) {
        return { status: 'cancelled' };
      }
      console.error(err);
      return { status: 'failed', message: passkeyLoginErrorMessage(err) };
    }
  }

  // Gemeinsame Nachbehandlung für Passwort- und Passkey-Login
  private completeLogin(user: APTechnicianHash, returnUrl: string) {
    this.setCurrentUser(user);
    this.router.navigate([returnUrl]);
  }

  public isLoggedIn() {
    const path = environment.apiURL + 'logged_in.json'

    return this.http.get<APTechnicianHash>(path)
  }

  public async logout() {
    const path = environment.apiURL + 'logout.json'

    try {
      const data = await firstValueFrom(this.http.post(path, {}));
      this.removeCurrentUser()
      this.router.navigate(['login'])
    } catch (err: unknown) {
      console.error(err);

      if (err instanceof HttpErrorResponse) {
        this.loginError = err.message;
      } else {
        this.loginError = "Ein Fehler ist aufgetreten."
      }

      this.router.navigate(['login'])
    }
  }

  // Logout mit Weiterleitung zur ursprünglichen Seite nach erneutem Login
  public async logoutWithReturnUrl(returnUrl: string) {
    const path = environment.apiURL + 'logout.json'

    this.removeCurrentUser()

    try {
      await firstValueFrom(this.http.post(path, {}));
    } catch {
      // Logout-Request fehlgeschlagen, Session ist aber lokal bereits bereinigt
    }

    this.router.navigate(['login'], { queryParams: { returnUrl } })
  }

  public loggedIn(): boolean {
    return !!this.getCurrentUser()
  }

  public rolePermissions() {
    const path = environment.apiURL + 'role_permissions.json'

    return this.http.get<{roles: string[]}>(path)
  }
}
